import CTABox from './cta-box';
import Comment from './comment';
import CommentingDisabledBox from './commenting-disabled-box';
import ContentTitle from './content-title';
import FocusedThread from './focused-thread';
import MainForm from './forms/main-form';
import Pagination from './pagination';
import {DESKTOP_MAX_THREAD_DEPTH, MOBILE_BREAKPOINT, MOBILE_MAX_THREAD_DEPTH} from '../../utils/helpers';
import {NavActionsContext} from '../../utils/nav-actions';
import {SortingForm} from './forms/sorting-form';
import {ThreadingContext} from '../../utils/threading-context';
import {getFocusedThread} from '../../utils/thread-graph';
import {useAppContext, useLabs} from '../../app-context';
import {useCommentNavigation} from './hooks/use-comment-navigation';
import {useEffect, useMemo, useRef, useState} from 'react';

const MOBILE_MEDIA_QUERY = `(max-width: ${MOBILE_BREAKPOINT - 1}px)`;

function getCurrentMaxThreadDepth() {
    if (typeof window === 'undefined' || !window.matchMedia) {
        return DESKTOP_MAX_THREAD_DEPTH;
    }

    return window.matchMedia(MOBILE_MEDIA_QUERY).matches ? MOBILE_MAX_THREAD_DEPTH : DESKTOP_MAX_THREAD_DEPTH;
}

const Content = () => {
    const labs = useLabs();
    const {pagination, comments, commentCount, title, showCount, commentsIsLoading, t, commentIdFromHash, showMissingCommentNotice, isMember, isPaidOnly, hasRequiredTier, isCommentingDisabled} = useAppContext();
    const containerRef = useRef<HTMLDivElement>(null);
    const [maxThreadDepth, setMaxThreadDepth] = useState(() => getCurrentMaxThreadDepth());

    useEffect(() => {
        if (typeof window === 'undefined' || !window.matchMedia) {
            return;
        }

        const mql = window.matchMedia(MOBILE_MEDIA_QUERY);
        const updateMaxThreadDepth = () => {
            setMaxThreadDepth(mql.matches ? MOBILE_MAX_THREAD_DEPTH : DESKTOP_MAX_THREAD_DEPTH);
        };

        updateMaxThreadDepth();
        mql.addEventListener('change', updateMaxThreadDepth);
        return () => mql.removeEventListener('change', updateMaxThreadDepth);
    }, []);

    const threadingContext = useMemo(() => ({
        maxThreadDepth
    }), [maxThreadDepth]);

    const isFirst = pagination?.total === 0;
    const canComment = isMember && hasRequiredTier && !isCommentingDisabled;

    // Explicit form/box visibility states
    const showMainForm = canComment;
    const showDisabledBox = !canComment && isCommentingDisabled;
    const showCtaBox = !canComment && !isCommentingDisabled;
    const useThreading = !!labs.commentsThreads;
    const focusedThread = useMemo(() => (
        useThreading ? getFocusedThread(comments, commentIdFromHash, maxThreadDepth) : null
    ), [comments, commentIdFromHash, maxThreadDepth, useThreading]);

    const navActions = useCommentNavigation({
        containerRef,
        focusedThread
    });

    const commentsComponents = comments.map(comment => <Comment key={comment.id} comment={comment} useThreading={useThreading} />);

    const initialsOf = (name: string) => (name.trim().split(/\s+/).map(w => w[0]).slice(0, 2).join('') || '?').toUpperCase();
    const teaserParticipants = useMemo(() => {
        const seen = new Map<string, string>();
        for (const comment of comments) {
            const m = comment.member;
            if (m?.id && !seen.has(m.id)) {
                seen.set(m.id, m.name || 'Anonymous');
            }
        }
        return Array.from(seen, ([id, name]) => ({id, name}));
    }, [comments]);
    const teaserExtra = teaserParticipants.length - 3;

    const teaser = (
        <div style={{position: 'relative', marginTop: '24px'}}>
            <div
                ref={containerRef}
                aria-hidden="true"
                data-testid="comment-elements"
                style={{
                    filter: 'blur(5px)',
                    opacity: 0.55,
                    userSelect: 'none',
                    pointerEvents: 'none',
                    maxHeight: '336px',
                    overflow: 'hidden',
                    paddingTop: '16px',
                    paddingLeft: '16px',
                    WebkitMaskImage: 'linear-gradient(to bottom, #000 0%, #000 35%, transparent 92%)',
                    maskImage: 'linear-gradient(to bottom, #000 0%, #000 35%, transparent 92%)'
                }}
            >
                {commentsComponents}
            </div>
            <div style={{position: 'absolute', left: 0, right: 0, bottom: '18px', display: 'flex', justifyContent: 'center'}}>
                <div style={{display: 'flex', alignItems: 'center', gap: '12px'}}>
                    <div style={{display: 'flex'}}>
                        {teaserParticipants.slice(0, 3).map((p, i) => (
                            <div
                                key={p.id}
                                style={{
                                    width: '30px',
                                    height: '30px',
                                    borderRadius: '50%',
                                    background: ['var(--fg)', 'var(--sub)', 'var(--muted)'][i % 3],
                                    color: 'var(--bg)',
                                    border: '2px solid var(--bg)',
                                    marginLeft: i === 0 ? 0 : '-8px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '12px',
                                    fontWeight: 600
                                }}
                            >
                                {initialsOf(p.name)}
                            </div>
                        ))}
                        {teaserExtra > 0 && (
                            <div
                                style={{
                                    width: '30px',
                                    height: '30px',
                                    borderRadius: '50%',
                                    background: 'var(--faint)',
                                    color: 'var(--sub)',
                                    border: '2px solid var(--bg)',
                                    marginLeft: '-8px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '12px',
                                    fontWeight: 600
                                }}
                            >
                                +{teaserExtra}
                            </div>
                        )}
                    </div>
                    <span style={{fontSize: '15px', color: 'var(--sub)'}}>
                        {teaserParticipants.length === 1
                            ? t('1 person is in the conversation')
                            : t('{amount} people are in the conversation', {amount: teaserParticipants.length})}
                    </span>
                </div>
            </div>
        </div>
    );

    const content = focusedThread ? (
        <>
            <ContentTitle count={commentCount} showCount={showCount} title={title}/>
            <div ref={containerRef} className={`z-10 transition-opacity duration-100 ${commentsIsLoading ? 'opacity-50' : ''}`} data-testid="comment-elements">
                <FocusedThread focusedThread={focusedThread} />
            </div>
        </>
    ) : (
        <>
            <ContentTitle count={commentCount} showCount={showCount} title={title}/>
            {showMissingCommentNotice && (
                <div className="mb-6 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 font-sans text-sm text-amber-900 dark:border-amber-500/40 dark:bg-amber-500/15 dark:text-amber-100" data-testid="missing-comment-notice">
                    {t('The linked comment is no longer available.')}
                </div>
            )}
            <div>
                {showMainForm && <MainForm commentsCount={comments.length} />}
                {showDisabledBox && (
                    <section className="flex flex-col items-center py-6 sm:px-8 sm:py-10" data-testid="commenting-disabled-box">
                        <CommentingDisabledBox />
                    </section>
                )}
                {showCtaBox && (
                    <section data-testid="cta-box">
                        <CTABox isFirst={isFirst} isPaid={isPaidOnly} />
                    </section>
                )}
            </div>
            {showCtaBox ? (
                comments.length > 0 ? teaser : null
            ) : (
                <>
                    {commentCount > 1 && (
                        <div className="z-20 mb-7 mt-3">
                            <span className="flex items-center gap-1.5 text-sm font-medium text-neutral-900 dark:text-neutral-100">
                                {t('Sort by')}: <SortingForm/>
                            </span>
                        </div>
                    )}
                    <div ref={containerRef} className={`z-10 transition-opacity duration-100 ${commentsIsLoading ? 'opacity-50' : ''}`} data-testid="comment-elements">
                        {commentsComponents}
                    </div>
                    <Pagination />
                </>
            )}
            {
                labs?.testFlag ? <div data-testid="this-comes-from-a-flag" style={{display: 'none'}}></div> : null
            }
        </>
    );

    return (
        <NavActionsContext.Provider value={navActions}>
            <ThreadingContext.Provider value={threadingContext}>
                {content}
            </ThreadingContext.Provider>
        </NavActionsContext.Provider>
    );
};

export default Content;
